// The till's cash shift (UC-60; brief T29, T30, T31): what the till's order
// store says of it, and the screen's calls to open, ask, close and print it
// again. Every rule is the store's (internal/core/domain/shift_stamp.go and
// internal/core/usecase/uc60_shift.go): the screen shows what it answers, and
// a till takes no payment while /store/state says so.

import { store } from '../api/client';
import type { components } from '../api/store';
import { fill, message, type Language } from '../i18n/messages';
import { readStoreState, storeState } from './storeState.svelte';

export type ShiftBody = components['schemas']['StoreShiftBody'];
export type ClosedShift = components['schemas']['StoreClosedShift'];
export type StateShift = components['schemas']['StoreStateShift'];
type OpenRequest = components['schemas']['StoreOpenShiftRequest'];
type CloseRequest = components['schemas']['StoreCloseShiftRequest'];

/** How often an open close asks the store how far its ask of the other tills got. */
export const ASK_POLL_MS = 1000;

/** The codes a payment, or a paid order's void, is refused with for want of a shift. */
const REFUSALS = new Set(['NO_SHIFT', 'SHIFT_STALE', 'CASH_POINT_UNREACHABLE']);

let current = $state<ShiftBody | null>(null);

/**
 * /store/state's shift: whether the till takes a payment now, and why not.
 * Undefined before the store's first answer, and from a store older than
 * shifts, which takes every payment as before.
 */
export function shiftState(): StateShift | undefined {
	return storeState()?.shift;
}

/** The store's last answer to GET /store/shift, or null before the first. */
export function shiftBody(): ShiftBody | null {
	return current;
}

/** Whether a refusal of a payment or a paid void is for want of a shift. */
export function shiftRefusal(code: string): boolean {
	return REFUSALS.has(code);
}

/** One row of Due at close by driver, as the screen shows it (T29, T31). */
export interface DueRow {
	/** Unique per row: the driver's id, or `none` and `other`, which come at most once. */
	key: string;
	label: string;
	count: number;
	amountMinor: number;
}

/**
 * Due at close split by driver (UC-60.R7; docs/spec/policy/reports.md, Due at
 * close by driver): each driver's open deliveries, then those with no driver,
 * then the open orders that are no delivery, in the order the store's close
 * gave them (`domain.SplitDue`). Empty for a close an older store wrote, which
 * shows Due at close as one line.
 */
export function dueRows(language: Language, closed: ClosedShift): DueRow[] {
	return (closed.dueByDriver ?? []).map((row, index) => ({
		// A driver's row always names its id; its place stands in should one not.
		key: row.kind === 'driver' ? row.driverId || `driver-${index}` : row.kind,
		label:
			row.kind === 'driver'
				? (row.name ?? '')
				: message(language, row.kind === 'none' ? 'shiftDueNoDriver' : 'shiftDueNotDelivery'),
		count: row.count,
		amountMinor: row.amountMinor,
	}));
}

/** Read the till's shift. Throws when the store does not answer it. */
export async function loadShift(): Promise<ShiftBody> {
	const { data, error } = await store.GET('/store/shift');
	if (error !== undefined || !data) throw new Error('the store did not read the shift');
	current = data;
	return data;
}

/**
 * One key per attempt. The same request sends the same key again, so a try
 * whose answer was lost is answered as before (INT-7); a changed request,
 * such as another amount typed, takes a new key. A manager's digits are no
 * part of the request, but who approves is, as the store's own fingerprint
 * of a close holds the approver: a close tried again with other digits by
 * the same manager is the same close, and one another manager approves is
 * another, which the store refuses SHIFT_CLOSED once the first went through.
 */
export function attemptKey(): (request: string) => string {
	let request = '';
	let key = '';
	return (next) => {
		if (key === '' || next !== request) {
			request = next;
			key = crypto.randomUUID();
		}
		return key;
	};
}

/** The open of one T30 sheet: POST /store/shift, keyed by the float typed. */
export function openShiftAttempt() {
	const key = attemptKey();
	return async (body: OpenRequest) => {
		try {
			return await store.POST('/store/shift', {
				body,
				params: { header: { 'Idempotency-Key': key(String(body.floatMinor)) } },
			});
		} finally {
			// The store's state after each write of the screen's (N14).
			void readStoreState();
		}
	};
}

/**
 * The close of one T31 sheet: POST /store/shift/close, keyed by the shift,
 * the count and the manager it is closed as: the one who approves, or the
 * one signed in, `signedIn`, as the store's fingerprint names them.
 */
export function closeShiftAttempt() {
	const key = attemptKey();
	return async (body: CloseRequest, signedIn: string) => {
		try {
			return await store.POST('/store/shift/close', {
				body,
				params: {
					header: {
						'Idempotency-Key': key(
							`${body.shiftId}|${body.countedMinor}|${body.approval?.userId ?? signedIn}`,
						),
					},
				},
			});
		} finally {
			void readStoreState();
		}
	};
}

/**
 * Ask the tills that send their cash here for the sales the till lacks, then
 * read the shift, whose `asked` says how far each ask got. The store answers
 * the ask at once and asks on its own (POST /store/shift/ask).
 */
export async function askShift(): Promise<ShiftBody> {
	const { error } = await store.POST('/store/shift/ask');
	if (error !== undefined) throw new Error('the store did not ask');
	return loadShift();
}

/** Whether a till asked is still asking. */
export function stillAsking(body: ShiftBody | null): boolean {
	return body?.asked.some((till) => till.state === 'asking') ?? false;
}

/** Read the shift every ASK_POLL_MS while a till is still asking. Returns the stop. */
export function watchAsk(): () => void {
	let stopped = false;
	let timer: ReturnType<typeof setTimeout> | undefined;
	const tick = async () => {
		try {
			await loadShift();
		} catch {
			// The last answer stands until the store answers again.
		}
		if (!stopped && stillAsking(current)) timer = setTimeout(() => void tick(), ASK_POLL_MS);
	};
	timer = setTimeout(() => void tick(), ASK_POLL_MS);
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

// The key of the Print again on its way, kept until the store answers it,
// so a tap after a lost answer prints no second copy (as printing.ts does),
// and the closed shift it was for: another shift's copy takes a new key.
let printKey = '';
let printFor = '';

/**
 * Print the last closed shift's report again, with its figures as they are
 * now (T29, T31): null once the store queued it, else the words for why not.
 */
export async function printShiftAgain(language: Language): Promise<string | null> {
	const shiftId = current?.lastClosed?.shiftId ?? '';
	if (printFor !== shiftId) printKey = '';
	printFor = shiftId;
	printKey ||= crypto.randomUUID();
	try {
		const { error, response } = await store.POST('/store/shift/print', {
			params: { header: { 'Idempotency-Key': printKey } },
		});
		printKey = '';
		if (error === undefined) return null;
		return message(
			language,
			response.status === 404 ? 'ordersNoReceiptPrinter' : 'shiftPrintFailed',
		);
	} catch {
		// No answer: the next tap sends the same key again.
		return message(language, 'shiftPrintFailed');
	}
}

/** The standing Notice of a till that takes no payment (T3, T8), and its action. */
export interface PayNotice {
	text: string;
	/** Open shift, on a till that is its own cash point; null on a pointing till. */
	action: string | null;
}

/** The Notice for the shift the store reports, or null while the till takes a payment. */
export function payNotice(language: Language, shift: StateShift | undefined): PayNotice | null {
	if (!shift || shift.payable) return null;
	if (shift.role === 'cash_point') {
		return { text: message(language, 'shiftNoShiftHere'), action: message(language, 'shiftOpen') };
	}
	const there = shift.cashPointName ?? '';
	switch (shift.reason) {
		case 'SHIFT_STALE':
			return { text: fill(message(language, 'shiftStale'), there), action: null };
		case 'CASH_POINT_UNREACHABLE':
			return { text: fill(message(language, 'shiftUnreachable'), there), action: null };
		default:
			return { text: fill(message(language, 'shiftNoShiftThere'), there), action: null };
	}
}

/**
 * The words for a paid order's void refused for want of a shift (T10): on a
 * cash point, that a shift must be open; on a pointing till, why it takes
 * no payment now, which holds a paid void back as well.
 */
export function voidRefusal(language: Language, shift: StateShift | undefined): string {
	const pointing = shift?.role === 'pointing' ? payNotice(language, shift) : null;
	return pointing?.text ?? message(language, 'shiftVoidNoShift');
}

/** Forget what the store said, for a test. */
export function resetShift(): void {
	current = null;
	printKey = '';
	printFor = '';
}
